// src/components/forms/InviteUser.jsx
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import { useAuth } from '../../context/AuthContext';
import { useMedicalCenter } from '../../context/MedicalCenterContext';
import { medicalCenterService } from '../../services/medicalCenterService';
import { userService } from '../../services/userService';


const InviteUser = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const { medicalCenterId, selectedMedicalCenter } = useMedicalCenter();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [medicalCenters, setMedicalCenters] = useState([]);

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: '',
    medical_center_id: ''
  });

  useEffect(() => {
    if (isOpen) {
      if (!medicalCenterId) {
        loadMedicalCenters();
      }
      resetForm();
    }
  }, [isOpen, medicalCenterId]);

  const loadMedicalCenters = async () => {
    try {
      const centers = await medicalCenterService.getMedicalCenters();
      setMedicalCenters(centers || []);
    } catch (error) {
      console.error('Error loading medical centers:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role: '',
      medical_center_id: medicalCenterId || ''
    });
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getAvailableRoles = () => {
    // Allow managers and admins to invite managers/doctors
    if (user?.role === 'admin' || user?.role === 'manager') {
      return [
        { value: 'manager', label: 'Manager' },
        { value: 'doctor', label: 'Doctor' }
      ];
    }
    return [];
  };

  const validateForm = () => {
    if (!formData.email) {
      setError('Email is required');
      return false;
    }
    if (!formData.role) {
      setError('Role is required');
      return false;
    }
    if (!medicalCenterId && !formData.medical_center_id) {
      setError('Medical Center is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      const invitationData = {
        email: formData.email,
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        role: formData.role,
        medical_center_id: medicalCenterId || formData.medical_center_id
      };

      await userService.inviteUser(invitationData);

      onSuccess('User invitation sent successfully!');
      onClose();
    } catch (error) {
      setError(error.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Invite User</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="user@example.com"
              required
            />
          </div>

          {!medicalCenterId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical Center *
              </label>
              <select
                name="medical_center_id"
                value={formData.medical_center_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Medical Center</option>
                {medicalCenters.map(center => (
                  <option key={center.id} value={center.id}>
                    {center.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {medicalCenterId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical Center
              </label>
              <div className="inline-flex items-center px-3 py-2 rounded-md bg-blue-50 text-blue-700 border border-blue-100">
                {selectedMedicalCenter?.name || medicalCenterId}
              </div>
              <input type="hidden" name="medical_center_id" value={medicalCenterId} />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Role</option>
              {getAvailableRoles().map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name (Optional)
            </label>
            <Input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              placeholder="Enter first name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name (Optional)
            </label>
            <Input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              placeholder="Enter last name"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default InviteUser;